import { apiService } from './api';
import { API_ENDPOINTS } from '../constants/api';

export interface LoginRequest {
  username: string;
  password: string;
}

export interface LoginResponse {
  token: string;
  refreshToken?: string;
  expiresAt?: string;
  user: {
    id: string;
    username: string;
    email: string;
    firstName: string;
    lastName: string;
    roles: string[];
    isActive: boolean;
    managerId?: string;
    roleTemplateId?: string;
    preferredIanaTimeZoneId?: string | null;
  };
}

export type User = {
  id: string;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  roles: string[];
  isActive: boolean;
  managerId?: string;
  roleTemplateId?: string;
  companyId?: string;
  businessUnitId?: string;
  departmentId?: string;
  departmentIds?: string[];
  departmentName?: string;
  departmentNames?: string[];
  /** UTC when the user last signed in (ISO date string). */
  lastLoginAt?: string;
  /** Date of birth (ISO date string). Used for birthday notifications. */
  dateOfBirth?: string;
  /** Work anniversary / date of engagement (ISO date string). Used for anniversary notifications and leave. */
  dateOfEngagement?: string;
  /** Last calendar day of employment (ISO date string). Cleared when reactivated. */
  dateOfExit?: string;
  /** IANA time zone for on-screen timestamps. Null/undefined means organisation time. */
  preferredIanaTimeZoneId?: string | null;
};

export const authService = {
  async login(username: string, password: string): Promise<LoginResponse> {
    const response = await apiService.post<LoginResponse>(API_ENDPOINTS.AUTH.LOGIN, { username, password });
    return response.data;
  },

  async refreshToken(refreshToken: string): Promise<LoginResponse> {
    const response = await apiService.post<LoginResponse>('/auth/refresh', { refreshToken });
    return response.data;
  },

  async revokeRefreshToken(refreshToken: string): Promise<void> {
    await apiService.post('/auth/revoke', { refreshToken }, { skipTokenRefresh: true });
  },

  async logout(): Promise<void> {
    await apiService.post(API_ENDPOINTS.AUTH.LOGOUT, undefined, { skipTokenRefresh: true });
  },

  async getCurrentUser(): Promise<User> {
    const response = await apiService.get<User>(API_ENDPOINTS.AUTH.ME);
    // Handle both camelCase and PascalCase for compatibility.
    const userData = response.data;
    interface UserDataWithRoles extends User {
      Roles?: string[];
      DepartmentName?: string;
      DepartmentNames?: string[];
    }
    const userWithPossibleRoles = userData as UserDataWithRoles;
    if (userData.roles || userWithPossibleRoles.Roles) {
      userData.roles = userData.roles || userWithPossibleRoles.Roles || [];
    }
    userData.departmentName = userData.departmentName || userWithPossibleRoles.DepartmentName;
    userData.departmentNames = userData.departmentNames || userWithPossibleRoles.DepartmentNames || [];
    return userData;
  }
};
